import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { recipesCopy } from '@/copy'
import { fillSlots } from '@/copy/slots'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { SelectField, TextField } from '@/ui/Field/Field'
import { Link } from '@/ui/Link/Link'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { providersApi } from '../../api/agents'
import {
  paramText,
  paramValue,
  type RecipeInstall,
  type RecipeParamField,
  type RecipeView,
  recipesApi,
} from '../../api/recipes'
import { ROUTES } from '../../constants/routes'
import {
  useAgentTemplatesQuery,
  useWorkspaceAgentsQuery,
} from '../../hooks/queries'
import { useTopicsQuery } from '../../hooks/useTopicsQuery'
import { invalidateRecipes } from './invalidateRecipes'
import { RecipeWarnings } from './RecipeWarnings'
import { SourcePicker } from './SourcePicker'

type Props = {
  workspaceId: string
  onClose: () => void
} & (
  | {
      /** Set this recipe up. */
      recipe: RecipeView
      initialSourceId?: string
      install?: undefined
    }
  | {
      /** Change this install; its source stays. */
      install: RecipeInstall
      /** The recipe, when the workspace still offers it. */
      recipe?: RecipeView
      initialSourceId?: undefined
    }
)

/**
 * Set a recipe up (or change a setup): the source its events come from,
 * the topic they land in, the agent that answers, and the recipe's own
 * parameters as the server describes them for that source.
 */
export function RecipeSetupCard(props: Props) {
  const { workspaceId, install, recipe, onClose } = props
  const qc = useQueryClient()
  const [search] = useSearchParams()
  const { data: topics = [] } = useTopicsQuery(workspaceId)
  const { data: agents = [] } = useWorkspaceAgentsQuery(workspaceId)
  const { data: templates = [] } = useAgentTemplatesQuery(workspaceId)
  const { data: providers } = useQuery({
    queryKey: ['providers', workspaceId],
    queryFn: () => providersApi.listAvailable(workspaceId),
    staleTime: 300_000,
  })
  const recipeId = install?.recipe_id ?? recipe?.id ?? ''
  const title = recipe?.title ?? install?.recipe_title ?? recipeId
  const defaultTopic = recipe?.default_topic_name ?? ''
  const [sourceId, setSourceId] = useState(
    install?.source.id ?? props.initialSourceId ?? '',
  )
  const [topicId, setTopicId] = useState(install?.topic_id ?? '')
  const [topicName, setTopicName] = useState(defaultTopic)
  // Back from creating the agent: it arrives preselected.
  const [agentId, setAgentId] = useState(
    search.get('agent') ?? install?.agent_id ?? '',
  )
  const [values, setValues] = useState<Record<string, string>>({})
  const [warnings, setWarnings] = useState<string[] | null>(null)

  const fieldsQuery = useQuery({
    queryKey: ['recipe-fields', workspaceId, recipeId, sourceId],
    queryFn: () => recipesApi.fields(workspaceId, recipeId, sourceId),
    enabled: !install && !!sourceId,
  })
  const fields: RecipeParamField[] = install
    ? install.param_fields
    : (fieldsQuery.data ?? [])
  const text = (f: RecipeParamField) =>
    values[f.key] ?? paramText(f, install?.params[f.key])

  const apply = useMutation({
    mutationFn: () => {
      const body = {
        agent_id: agentId,
        ...(topicId
          ? { topic_id: topicId }
          : { topic_name: topicName.trim() || defaultTopic }),
        params: Object.fromEntries(
          fields.map((f) => [f.key, paramValue(f, text(f))]),
        ),
      }
      return install
        ? recipesApi.update(workspaceId, install.id, body)
        : recipesApi.install(workspaceId, {
            ...body,
            recipe_id: recipeId,
            source_id: sourceId,
          })
    },
    onSuccess: (saved) => {
      invalidateRecipes(qc, workspaceId)
      if (saved.warnings.length > 0) setWarnings(saved.warnings)
      else onClose()
    },
  })

  // The agent form returns here on save with the new agent selected.
  const template = templates.find((t) => t.id === recipe?.suggested_template)
  const newAgentState = {
    wsId: workspaceId,
    returnTo: install
      ? ROUTES.recipeInstall(workspaceId, install.id)
      : ROUTES.recipeSetup(workspaceId, recipeId, sourceId || undefined),
    ...(template ? { template } : {}),
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (agentId && sourceId) apply.mutate()
  }

  const header = <Text weight="strong">{title}</Text>

  if (warnings) {
    return (
      <Card
        header={header}
        footer={
          <Button ground="surface" onClick={onClose}>
            {recipesCopy.setup.done}
          </Button>
        }
      >
        <RecipeWarnings
          lead={recipesCopy.setup.setUpWithWarnings}
          warnings={warnings}
        />
      </Card>
    )
  }

  const id = (part: string) => `recipe-${recipeId}-${part}`
  return (
    <Card header={header}>
      <form onSubmit={submit}>
        <Stack gap="space-12">
          {recipe && <Text tone="text-mute">{recipe.description}</Text>}

          {install ? (
            <TextField
              id={id('source')}
              label={recipesCopy.setup.source}
              readOnly
              value={install.source.name}
              onValue={() => undefined}
            />
          ) : (
            recipe && (
              <SourcePicker
                workspaceId={workspaceId}
                recipe={recipe}
                value={sourceId}
                onValue={(v) => {
                  setSourceId(v)
                  setValues({})
                }}
              />
            )
          )}

          <SelectField
            id={id('topic')}
            label={recipesCopy.setup.topic}
            value={topicId}
            onValue={setTopicId}
          >
            <option value="">{recipesCopy.setup.newTopic}</option>
            {topics.map((t) => (
              <option key={t.topic_id} value={t.topic_id}>
                {recipesCopy.install.topicName(t.name)}
              </option>
            ))}
          </SelectField>
          {!topicId && (
            <TextField
              id={id('topic-name')}
              label={recipesCopy.setup.topicName}
              value={topicName}
              onValue={setTopicName}
              inputProps={{ placeholder: defaultTopic }}
            />
          )}

          <SelectField
            id={id('agent')}
            label={recipesCopy.setup.agent}
            value={agentId}
            onValue={setAgentId}
          >
            <option value="">{recipesCopy.setup.chooseAgent}</option>
            {agents.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>
                {a.name}
              </option>
            ))}
          </SelectField>
          {/* A way somewhere, not a statement about the field: outside
              its help (Field card). */}
          <Text as="p">
            <Link href={ROUTES.agentNew(workspaceId)} state={newAgentState}>
              {recipesCopy.setup.newAgent}
            </Link>
          </Text>

          {fields.map((f) => {
            const set = (v: string) =>
              setValues((prev) => ({ ...prev, [f.key]: v }))
            return f.kind === 'select' ? (
              <SelectField
                key={f.key}
                id={id(f.key)}
                label={f.label}
                value={text(f)}
                onValue={set}
                help={f.help ?? undefined}
              >
                {f.options.map((o) => (
                  <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}
                  </option>
                ))}
              </SelectField>
            ) : (
              <TextField
                key={f.key}
                id={id(f.key)}
                label={f.label}
                value={text(f)}
                onValue={set}
                help={f.help ?? undefined}
                inputProps={{ placeholder: f.placeholder ?? undefined }}
              />
            )
          })}

          {providers?.length === 0 && (
            <Alert tone="warning">
              {fillSlots(recipesCopy.setup.noProvider, {
                settings: (
                  <Link href={ROUTES.workspaceSettings(workspaceId)}>
                    {recipesCopy.setup.settingsLink}
                  </Link>
                ),
              })}
            </Alert>
          )}
          {apply.isError && (
            <Alert tone="danger">
              {(install
                ? recipesCopy.setup.saveFailed
                : recipesCopy.setup.setUpFailed)(
                apply.error instanceof Error
                  ? apply.error.message
                  : String(apply.error),
              )}
            </Alert>
          )}
          <Inline gap="space-8">
            <Button
              type="submit"
              ground="surface"
              loading={apply.isPending}
              disabled={!agentId || !sourceId}
            >
              {install ? recipesCopy.setup.save : recipesCopy.setup.submit}
            </Button>
            <Button variant="outline" ground="surface" onClick={onClose}>
              {recipesCopy.setup.cancel}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Card>
  )
}
